import { useEffect, useMemo, useRef, useState } from 'react';

import * as api from '@/features/wiki/api';
import { WikiSpace } from '@/features/wiki/types';
import { useAppData } from '@/app-data';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { SearchField } from '@/components/ui/SearchField';
import { Eye } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import { cn } from '@/components/ui/cn';

/**
 * The "cog" space-settings modal (same shell as the board-settings modal): rename/icon/description
 * (General), a watch toggle, and a members/admins section (mirrors BoardAccessSection). The members
 * section silently hides for non-managers (the access fetch 403s).
 */
export default function WikiSpaceSettings({ space, onClose, onSaved, onDeleted }: { space: WikiSpace; onClose: () => void; onSaved: (s: WikiSpace) => void; onDeleted: () => void }) {
  return (
    <Modal open onClose={onClose} title="Space settings" className="max-w-[560px]">
      <GeneralSection space={space} onSaved={onSaved} />
      <WatchSection space={space} />
      <MembersSection spaceId={space.id} />
      <DangerSection space={space} onDeleted={onDeleted} />
    </Modal>
  );
}

/** Danger zone: delete the whole space (and, on the server, its pages). Two-step confirm; the backend
 *  refuses to delete the last remaining space (surfaced as a friendly message). */
function DangerSection({ space, onDeleted }: { space: WikiSpace; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const del = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteWikiSpace(space.id);
      onDeleted();
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 409 ? 'This is your only space — create another before deleting it.' : 'Could not delete this space. Please try again.');
      setConfirming(false);
      setBusy(false);
    }
  };

  return (
    <section className="mt-6 border-t border-line pt-5">
      <div className="mb-1 text-[13px] font-semibold text-danger">Delete space</div>
      <p className="mb-3 text-[12px] text-fg-faint">
        Permanently deletes <span className="font-medium text-fg-soft">{space.name}</span> and all of its pages. This can’t be undone.
      </p>
      {error ? <p className="mb-2 text-[12px] font-medium text-danger">{error}</p> : null}
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={del}
            disabled={busy}
            className="inline-flex h-8 items-center rounded-lg bg-danger px-3 text-[12.5px] font-semibold text-white transition-colors hover:brightness-110 disabled:opacity-50"
          >
            {busy ? 'Deleting…' : `Yes, delete “${space.name}”`}
          </button>
          <button type="button" onClick={() => setConfirming(false)} disabled={busy} className="text-[12px] text-fg-faint hover:text-fg">
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="inline-flex h-8 items-center rounded-lg border border-danger/40 px-3 text-[12.5px] font-semibold text-danger transition-colors hover:bg-danger/10"
        >
          Delete space…
        </button>
      )}
    </section>
  );
}

function GeneralSection({ space, onSaved }: { space: WikiSpace; onSaved: (s: WikiSpace) => void }) {
  const [name, setName] = useState(space.name);
  const [icon, setIcon] = useState(space.icon ?? '');
  const [description, setDescription] = useState(space.description ?? '');
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');

  const save = async () => {
    if (!name.trim()) return;
    setSaving('saving');
    try {
      const s = await api.updateWikiSpace(space.id, { name: name.trim(), icon: icon.trim() || null, description: description.trim() });
      onSaved(s);
      setSaving('saved');
      setTimeout(() => setSaving('idle'), 1600);
    } catch {
      setSaving('idle');
    }
  };

  return (
    <section className="mb-6 border-b border-line pb-6">
      <div className="mb-3 text-[13px] font-semibold text-fg-strong">General</div>
      <label className="mb-1 block text-[11.5px] font-medium text-fg-muted">Name</label>
      <div className="mb-3 flex items-center gap-2">
        <input
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          maxLength={2}
          placeholder="📄"
          className="h-9 w-11 rounded-lg border border-line bg-surface-inset text-center text-[18px] outline-none focus:border-line-strong"
          title="Icon (emoji)"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-9 flex-1 rounded-lg border border-line bg-surface-inset px-3 text-[14px] text-fg outline-none focus:border-line-strong"
        />
      </div>
      <p className="mb-3 -mt-1 text-[11px] text-fg-faint">
        URL: <span className="font-mono text-fg-muted">/wiki/{space.slug}</span> · updates to match the name (previously-shared links to the old URL stop working).
      </p>
      <label className="mb-1 block text-[11.5px] font-medium text-fg-muted">Description</label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        className="mb-3 w-full resize-none rounded-lg border border-line bg-surface-inset px-3 py-2 text-[13px] text-fg outline-none focus:border-line-strong"
        placeholder="What this space is for…"
      />
      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={save} disabled={saving === 'saving' || !name.trim()}>
          {saving === 'saving' ? 'Saving…' : 'Save'}
        </Button>
        {saving === 'saved' ? <span className="text-[11px] font-medium text-emerald-500">Saved</span> : null}
      </div>
    </section>
  );
}

function WatchSection({ space }: { space: WikiSpace }) {
  const [watching, setWatching] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listMyWatches()
      .then((ws) => {
        if (!cancelled) {
          setWatching(ws.some((w) => w.target_type === 'wiki_space' && w.target_id === space.id));
          setLoaded(true);
        }
      })
      .catch(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [space.id]);

  const toggle = async () => {
    if (busy.current) return;
    busy.current = true;
    const next = !watching;
    setWatching(next);
    try {
      await api.setWatch('wiki_space', space.id, next);
    } catch {
      setWatching(!next); // revert
    } finally {
      busy.current = false;
    }
  };

  return (
    <section className="mb-6 flex items-center justify-between gap-4 border-b border-line pb-6">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-fg-strong">Watch this space</div>
        <p className="mt-0.5 text-[12px] text-fg-faint">Get its new pages + edits in your periodic digest.</p>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={!loaded}
        aria-pressed={watching}
        className={cn(
          'inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
          watching ? 'bg-accent/15 text-accent' : 'bg-layer text-fg-soft hover:bg-layer-strong',
        )}
      >
        <Eye className="h-4 w-4" />
        {watching ? 'Watching' : 'Watch space'}
      </button>
    </section>
  );
}

function MembersSection({ spaceId }: { spaceId: string }) {
  const { members } = useAppData();
  const [mode, setMode] = useState<'all' | 'restricted'>('all');
  const [access, setAccess] = useState<Set<string>>(new Set());
  const [admins, setAdmins] = useState<Set<string>>(new Set());
  const [state, setState] = useState<'loading' | 'ready' | 'hidden'>('loading');
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    api
      .getWikiSpaceAccess(spaceId)
      .then((a) => {
        if (cancelled) return;
        setMode(a.access_mode === 'restricted' ? 'restricted' : 'all');
        setAccess(new Set(a.members.map((m) => m.member_id)));
        setAdmins(new Set(a.members.filter((m) => m.role === 'admin').map((m) => m.member_id)));
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('hidden');
      });
    return () => {
      cancelled = true;
    };
  }, [spaceId]);

  const activeMembers = useMemo(() => [...members].filter((m) => m.active).sort((a, b) => a.name.localeCompare(b.name)), [members]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return activeMembers;
    return activeMembers.filter((m) => `${m.name} ${m.email ?? ''}`.toLowerCase().includes(s));
  }, [activeMembers, q]);

  const toggleAccess = (id: string) =>
    setAccess((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
        setAdmins((a) => {
          const na = new Set(a);
          na.delete(id);
          return na;
        });
      } else n.add(id);
      return n;
    });
  const toggleAdmin = (id: string) =>
    setAdmins((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else {
        n.add(id);
        if (mode === 'restricted') setAccess((a) => new Set(a).add(id));
      }
      return n;
    });

  const save = async () => {
    setSaving('saving');
    const ids = mode === 'all' ? [...admins] : [...new Set([...access, ...admins])];
    const payload = ids.map((id) => ({ space_id: spaceId, member_id: id, role: admins.has(id) ? 'admin' : 'member' }));
    try {
      const res = await api.setWikiSpaceAccess(spaceId, { access_mode: mode, members: payload });
      setMode(res.access_mode === 'restricted' ? 'restricted' : 'all');
      setAccess(new Set(res.members.map((m) => m.member_id)));
      setAdmins(new Set(res.members.filter((m) => m.role === 'admin').map((m) => m.member_id)));
      setSaving('saved');
      setTimeout(() => setSaving('idle'), 1600);
    } catch {
      setSaving('idle');
    }
  };

  if (state !== 'ready') return null;

  const seg = (active: boolean) => cn('flex-1 rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors', active ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-muted hover:text-fg');

  return (
    <section>
      <div className="mb-1 flex items-center gap-2">
        <div className="text-[13px] font-semibold text-fg-strong">Members &amp; admins</div>
        <div className="ml-auto flex items-center gap-2">
          {saving === 'saved' ? <span className="text-[11px] font-medium text-emerald-500">Saved</span> : null}
          <Button variant="primary" size="sm" onClick={save} disabled={saving === 'saving'}>
            {saving === 'saving' ? 'Saving…' : 'Save access'}
          </Button>
        </div>
      </div>
      <p className="mb-3 text-[12px] text-fg-faint">Who can open this space, and who can manage its settings.</p>

      <div className="mb-3 inline-flex w-full max-w-[320px] rounded-lg bg-layer p-0.5">
        <button type="button" className={seg(mode === 'all')} onClick={() => setMode('all')}>
          All members
        </button>
        <button type="button" className={seg(mode === 'restricted')} onClick={() => setMode('restricted')}>
          Only selected
        </button>
      </div>

      <SearchField value={q} onChange={setQ} placeholder="Search members…" />
      <div className="thin-scroll mt-2 max-h-[260px] overflow-auto rounded-xl border border-line">
        {shown.map((m) => {
          const hasAccess = mode === 'all' || access.has(m.id);
          const isAdmin = admins.has(m.id);
          return (
            <div key={m.id} className="flex items-center gap-2.5 border-b border-line px-3 py-2 last:border-b-0">
              <MemberAvatar member={m} size={26} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-fg">{m.name}</div>
                <div className="truncate text-[11px] text-fg-faint">{m.email}</div>
              </div>
              {mode === 'restricted' ? (
                <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-fg-muted">
                  <input type="checkbox" checked={access.has(m.id)} onChange={() => toggleAccess(m.id)} className="accent-accent" />
                  Access
                </label>
              ) : (
                <span className="text-[11px] text-fg-ghost">Has access</span>
              )}
              <label className={cn('flex items-center gap-1.5 text-[12px]', hasAccess ? 'cursor-pointer text-fg-muted' : 'cursor-not-allowed text-fg-ghost')}>
                <input type="checkbox" checked={isAdmin} disabled={!hasAccess} onChange={() => toggleAdmin(m.id)} className="accent-accent" />
                Admin
              </label>
            </div>
          );
        })}
        {shown.length === 0 ? <div className="px-3 py-6 text-center text-[12px] text-fg-faint">No members match.</div> : null}
      </div>
    </section>
  );
}
